import React from 'react';
import { Box, Card, CardContent } from '@mui/material';
import { ReportPhotoDTO } from '../types';
import { usePhotoItem } from '../hooks';
import ImageWithFallback from './ImageWithFallback';
import FullScreenImageDialog from './FullScreenImageDialog';
import PhotoEditorDialog from './PhotoEditorDialog';
import CaptionEditor from './CaptionEditor';
import PhotoActions from './PhotoActions';

interface PhotoItemProps<TPhoto extends ReportPhotoDTO = ReportPhotoDTO> {
  photo: TPhoto;
  onUpdateCaption: (photoId: string, caption: string) => void;
  onDelete: (photoId: string) => Promise<void>;
  onEditPhoto?: (photoId: string, editedBlob: Blob, editedUrl: string) => Promise<void>;
  onRotate?: () => void;
  onSaveRotation?: () => Promise<void>;
  pendingRotation?: number;
  isSavingRotation?: boolean;
  isSelected?: boolean;
  onSelect?: (photoId: string, selected: boolean) => void;
  thumbnailUrl: string;
  localCaption?: string;
  onCaptionChange?: (photoId: string, caption: string) => void;
  onMoveToNext?: () => void;
  onMoveToPrevious?: () => void;
  onMoveToBottom?: () => void;
  onRegisterEditTrigger?: (triggerEdit: () => void) => void;
  getImageAsBase64: (blobName: string) => Promise<string>;
  isReadOnly?: boolean;
}

const PhotoItem = <TPhoto extends ReportPhotoDTO = ReportPhotoDTO>({
  photo,
  onUpdateCaption,
  onDelete,
  onEditPhoto,
  onRotate,
  onSaveRotation,
  pendingRotation = 0,
  isSavingRotation = false,
  isSelected = false,
  onSelect,
  thumbnailUrl,
  localCaption,
  onCaptionChange,
  onMoveToNext,
  onMoveToPrevious,
  onMoveToBottom,
  onRegisterEditTrigger,
  getImageAsBase64,
  isReadOnly = false,
}: PhotoItemProps<TPhoto>) => {
  const [isPhotoEditorOpen, setIsPhotoEditorOpen] = React.useState(false);
  const [visualRotation, setVisualRotation] = React.useState(0);
  /** When false, transform changes apply instantly — avoids animating 180°→0° through ~90° after save. */
  const [rotationTransformTransition, setRotationTransformTransition] = React.useState('transform 0.3s ease');
  const [containerWidth, setContainerWidth] = React.useState(0);
  const photoContainerRef = React.useRef<HTMLDivElement>(null);
  const pendingRotationReset = React.useRef(false);
  const restoreRotationTransitionRaf = React.useRef<number | null>(null);
  const prevPendingRotation = React.useRef(pendingRotation);
  const prevThumbnailUrl = React.useRef(thumbnailUrl);

  const snapVisualRotation = React.useCallback((degrees: number) => {
    if (restoreRotationTransitionRaf.current != null) {
      cancelAnimationFrame(restoreRotationTransitionRaf.current);
      restoreRotationTransitionRaf.current = null;
    }
    setRotationTransformTransition('none');
    setVisualRotation(degrees);
    restoreRotationTransitionRaf.current = requestAnimationFrame(() => {
      restoreRotationTransitionRaf.current = requestAnimationFrame(() => {
        setRotationTransformTransition('transform 0.3s ease');
        restoreRotationTransitionRaf.current = null;
      });
    });
  }, []);

  const {
    isEditing,
    isDeleting,
    isFullScreenOpen,
    handleEditClick,
    handlePhotoClick,
    handleSaveCaption,
    handleCancelEdit,
    handleDeleteClick,
    handleCloseFullScreen,
  } = usePhotoItem({
    photoId: photo.id,
    originalBlobName: photo.originalBlobName,
    onUpdateCaption,
    onDelete,
    onCaptionChange,
  });

  const handleEditPhotoClick = async () => {
    try {
      setIsPhotoEditorOpen(true);
    } catch (error) {
      console.error('Error loading original image for editing:', error);
    }
  };

  const handleClosePhotoEditor = () => {
    setIsPhotoEditorOpen(false);
  };

  const handleSaveEditedPhoto = async (editedBlob: Blob, editedUrl: string) => {
    if (onEditPhoto) {
      await onEditPhoto(photo.id, editedBlob, editedUrl);
    }
    setIsPhotoEditorOpen(false);
  };

  // useLayoutEffect runs before any browser onLoad event can fire — eliminates the
  // race where a cached image loads before the ref flag is set.
  React.useLayoutEffect(() => {
    const prevRotation = prevPendingRotation.current;
    const prevUrl = prevThumbnailUrl.current;
    prevPendingRotation.current = pendingRotation;
    prevThumbnailUrl.current = thumbnailUrl;

    if (pendingRotation > 0 && pendingRotation !== prevRotation) {
      // Click added OR partial save reduced pending — keep CSS = remaining pending
      // so image (already partially rotated on server) + CSS always sums to the correct total
      setVisualRotation(pendingRotation * 90);
    } else if (pendingRotation === 0 && prevRotation > 0) {
      if (thumbnailUrl !== prevUrl) {
        // Full save completed + new image incoming — hold CSS until image loads
        pendingRotationReset.current = true;
      } else {
        // Cancelled — no new image coming, reset CSS immediately
        snapVisualRotation(0);
      }
    }
  }, [pendingRotation, thumbnailUrl, snapVisualRotation]);

  const handleImageLoad = () => {
    if (pendingRotationReset.current) {
      pendingRotationReset.current = false;
      snapVisualRotation(0);
    }
  };

  // Register the edit trigger function
  React.useEffect(() => {
    if (onRegisterEditTrigger) {
      onRegisterEditTrigger(handleEditClick);
    }
  }, [onRegisterEditTrigger, handleEditClick]);

  React.useEffect(() => {
    return () => {
      if (restoreRotationTransitionRaf.current != null) {
        cancelAnimationFrame(restoreRotationTransitionRaf.current);
      }
    };
  }, []);

  React.useEffect(() => {
    const el = photoContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handlePhotoClickWithSave = React.useCallback(async () => {
    if (pendingRotation > 0 && onSaveRotation) {
      await onSaveRotation();
    }
    handlePhotoClick();
  }, [pendingRotation, onSaveRotation, handlePhotoClick]);

  // Determine if photo has caption (use local state if available)
  const hasCaption = localCaption !== undefined ? localCaption.trim().length > 0 : photo.hasCaption;
  const displayCaption = localCaption !== undefined ? localCaption : photo.caption;

  return (
    <>
      <Card
        onClick={() => onSelect?.(photo.id, !isSelected)}
        sx={{
          ...styles.card,
          border: isSelected ? '2px solid' : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
        }}
      >
        <Box
          ref={photoContainerRef}
          sx={{
            ...styles.photoContainer,
            '& img': {
              transform: `rotate(${visualRotation}deg)${visualRotation % 180 !== 0 && containerWidth > 0 ? ` scale(${containerWidth / 300})` : ''}`,
              transition: rotationTransformTransition,
              transformOrigin: 'center center',
            },
          }}
        >
          <ImageWithFallback
            src={thumbnailUrl || ''}
            alt={displayCaption && displayCaption.trim().length > 0 ? displayCaption : photo.fileName}
            onClick={handlePhotoClickWithSave}
            onLoad={handleImageLoad}
            height={300}
          />
        </Box>

        {!isReadOnly && (
          <PhotoActions
            onEdit={handleEditPhotoClick}
            onDelete={handleDeleteClick}
            onMoveToBottom={onMoveToBottom}
            onRotate={onRotate}
            onSaveRotation={onSaveRotation}
            pendingRotation={pendingRotation}
            isSavingRotation={isSavingRotation}
            isDeleting={isDeleting}
          />
        )}

        <CardContent sx={styles.cardContent}>
          <CaptionEditor
            caption={displayCaption || ''}
            hasCaption={hasCaption}
            onSave={handleSaveCaption}
            onCancel={handleCancelEdit}
            isEditing={isEditing}
            onEditClick={handleEditClick}
            onMoveToNext={onMoveToNext}
            onMoveToPrevious={onMoveToPrevious}
            isReadOnly={isReadOnly}
          />
        </CardContent>
      </Card>

      <FullScreenImageDialog
        open={isFullScreenOpen}
        onClose={handleCloseFullScreen}
        originalBlobName={photo.originalBlobName}
        alt={displayCaption && displayCaption.trim().length > 0 ? displayCaption : photo.fileName}
        getImageAsBase64={getImageAsBase64}
      />

      <PhotoEditorDialog
        open={isPhotoEditorOpen}
        onClose={handleClosePhotoEditor}
        originalBlobName={photo.originalBlobName}
        fileName={photo.fileName}
        onSave={handleSaveEditedPhoto}
        getImageAsBase64={getImageAsBase64}
      />
    </>
  );
};

const styles = {
  card: {
    position: 'relative' as const,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    overflow: 'hidden',
    '&:hover': {
      boxShadow: 4,
      transform: 'translateY(-2px)',
    },
  },
  photoContainer: {
    position: 'relative' as const,
    height: 300,
    overflow: 'hidden',
  },
  cardContent: {
    p: '0.5rem 1.5rem !important',
  },
};

export default PhotoItem;
